import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { initiateLogin, initiateLogout } from './oauth';
import { getCurrentUserLabel, getValidToken } from './tokens';

interface AuthContextType {
  getValidToken: () => Promise<string | null>;
  logout: () => Promise<void>;
  userLabel: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

type Status = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Gate the app behind a login. With no usable token we redirect straight to
 * the hosted login page (there's nothing to show an anonymous user).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let cancelled = false;
    getValidToken().then(token => {
      if (cancelled) return;
      setStatus(token ? 'authenticated' : 'unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status === 'unauthenticated') {
      initiateLogin().catch(console.error);
    }
  }, [status]);

  const getValidTokenCallback = useCallback(() => getValidToken(), []);
  const logout = useCallback(() => initiateLogout(), []);

  const value = useMemo<AuthContextType>(
    () => ({
      getValidToken: getValidTokenCallback,
      logout,
      userLabel: getCurrentUserLabel(),
    }),
    [getValidTokenCallback, logout]
  );

  if (status !== 'authenticated') {
    return (
      <p className="status">
        {status === 'loading' ? 'Checking login…' : 'Redirecting to login…'}
      </p>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
